/**
 * Flag "ya se mostró el aviso de ubicación no disponible" (004 · US6 · T068; FR-003, US6 AC4).
 *
 * El aviso ("No pudimos obtener tu ubicación. Puedes continuar") es **no modal** y nunca bloquea la
 * marcación; solo se muestra **una vez por sesión de navegador** para no repetirlo en cada
 * reintento si el permiso sigue denegado. Mismo patrón que `features/access/privacy-store.ts`:
 * `sessionStorage` (nunca `localStorage`), `useSyncExternalStore` para la reactividad.
 */
import { useSyncExternalStore } from 'react';

export const LOCATION_NOTICE_SHOWN_KEY = 'fermin:location-notice-shown';

const listeners = new Set<() => void>();

function readShown(): boolean {
  try {
    return globalThis.sessionStorage?.getItem(LOCATION_NOTICE_SHOWN_KEY) === '1';
  } catch {
    return false;
  }
}

let shown = readShown();

export function hasShownLocationNotice(): boolean {
  return shown;
}

/** Marca el aviso como ya mostrado; no vuelve a aparecer el resto de la sesión. */
export function setLocationNoticeShown(value: boolean): void {
  if (shown === value) {
    return;
  }
  shown = value;
  try {
    if (value) {
      globalThis.sessionStorage?.setItem(LOCATION_NOTICE_SHOWN_KEY, '1');
    } else {
      globalThis.sessionStorage?.removeItem(LOCATION_NOTICE_SHOWN_KEY);
    }
  } catch {
    // Sin almacenamiento (modo privado estricto): el flag vive solo en memoria.
  }
  for (const listener of listeners) {
    listener();
  }
}

export function subscribeLocationNoticeShown(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Hook reactivo, por si alguna pantalla necesita leerlo directamente. */
export function useLocationNoticeShown(): boolean {
  return useSyncExternalStore(subscribeLocationNoticeShown, hasShownLocationNotice, () => false);
}
